/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useContext, useRef } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

// @mui icons
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "context/authContext";
import { getImageSrc } from "services/utils";
import MDButton from "components/MDButton";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
} from "@mui/material";
import { updateEmployee } from "services/api";
import { LockReset } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { changePassword } from "services/api";
import { fieldStyles } from "services/utils";

// Context

function Overview() {
  const { t } = useTranslation();
  const { employee, setEmployee } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // "success" or "error"

  const [editedData, setEditedData] = useState({
    name: employee?.name || "",
    email: employee?.email || "",
    phoneNumber: employee?.phoneNumber || "",
    password: "",
    newPassword: "",
    confirmPassword: "",
    dob: employee?.dob || "",
    language: employee?.language || "",
  });

  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Add these handler functions
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordSubmit = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbarMessage(t("user.profile.snackbar.passwordMismatch"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    setPasswordLoading(true);
    
    let passwordChange = await changePassword(
      employee.id,
      passwordData.currentPassword,
      passwordData.newPassword
    );

    if (passwordChange == "success") {
      // Optionally show success message
      setSnackbarMessage(t("user.profile.snackbar.passwordSuccess"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } else {
      if (passwordChange[0] === "i") {
        setSnackbarMessage(t("user.profile.snackbar.passwordIncorrect"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else if (passwordChange === "length") {
        setSnackbarMessage(t("user.profile.snackbar.passwordTooShort"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else if (passwordChange === "different") {
        setSnackbarMessage(t("user.profile.snackbar.passwordSame"));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        // Optionally show success message
        setSnackbarMessage(passwordChange);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
    // Clear the form and close dialog
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setOpenPasswordDialog(false);
    setPasswordLoading(false);
  };

  const fileInputRef = useRef(null);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedData({
      ...editedData,
      password: "",
      newPassword: "",
      confirmPassword: "",
    });
  };

  const handleSaveClick = async () => {
    setLoading(true);

    const updatedEmployee = {
      ...employee,
      name: editedData.name,
      email: editedData.email,
      phoneNumber: editedData.phoneNumber,
      dob: editedData.dob ? new Date(editedData.dob) : null,
      language: editedData.language,
    };
    await updateEmployee(updatedEmployee);
    setEmployee(updatedEmployee);
    setIsEditing(false);
    setLoading(false);

    setSnackbarMessage(t("user.profile.snackbar.updateSuccess"));
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = event.target.files[0];

    if (file) {
      setPhotoLoading(true);
      // Validate file type (only images)
      if (!file.type.startsWith("image/")) {
        setSnackbarMessage(t("admin.employees.employeeDialog.validation.photoInvalidType"));
        setSnackbarOpen(true);
        setPhotoLoading(false);

        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setSnackbarMessage(t("admin.employees.employeeDialog.validation.photoTooLarge"));
        setSnackbarOpen(true);
        setPhotoLoading(false);

        return;
      }

      // Read the file as Base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result.split(",")[1]; // Remove prefix
        try {
          const updatedEmployee = {
            ...employee,
            photo: base64String,
          };
          await updateEmployee(updatedEmployee);
          // Optionally show success message
          setEmployee(updatedEmployee);
          setSnackbarMessage(t("user.profile.snackbar.photoSuccess"));
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setPhotoLoading(false);
        } catch (error) {
          setSnackbarMessage(t("user.profile.snackbar.photoFailure"));
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          setPhotoLoading(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <MDBox mb={2} />
      <MDBox position="relative" mb={5}>
        <Card
          sx={{
            position: "relative",
            p: 4,
            borderRadius: 3,
            boxShadow: 3,
          }}
        >
          <MDBox mt={5} mb={3}>
            <Grid container spacing={4}>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" }, // Column on mobile, row on tablet+
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "flex-start" }, // Center on mobile, left-align on tablet+
                  gap: 2, // Adds consistent spacing between elements
                  p: { xs: 2, sm: 0 }, // Add padding on mobile
                  width: "100%",
                }}
              >
                {photoLoading ? (
                  <CircularProgress size={30} />
                ) : (
                  <MDBox
                    position="relative"
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: { xs: 120, sm: 150 }, // Smaller on mobile
                      width: { xs: 120, sm: 150 }, // Smaller on mobile
                      minWidth: { xs: 120, sm: 150 }, // Prevent shrinking
                      mx: { xs: 0, sm: 2 }, // Horizontal margin only on tablet+
                    }}
                  >
                    <Avatar
                      src={getImageSrc(employee?.photo)}
                      sx={{
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                      onClick={handleAvatarClick}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        backgroundColor: "background.paper",
                        "&:hover": {
                          backgroundColor: "background.default",
                        },
                        width: { xs: 32, sm: 40 }, // Smaller on mobile
                        height: { xs: 32, sm: 40 }, // Smaller on mobile
                      }}
                      onClick={handleAvatarClick}
                    >
                      <CameraAltIcon />
                    </IconButton>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      accept="image/*"
                      style={{ display: "none" }}
                    />
                  </MDBox>
                )}
              </Grid>
              <Grid item xs={12} md={8}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <MDTypography variant="h5">{t("user.profile.title")}</MDTypography>
                  {!isEditing ? (
                    <MDButton color="primary" startIcon={<EditIcon />} onClick={handleEditClick}>
                      {t("user.profile.edit")}
                    </MDButton>
                  ) : loading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center">
                      <CircularProgress size={20} />
                    </MDBox>
                  ) : (
                    <MDBox>
                      <MDButton
                        variant="contained"
                        color="info"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveClick}
                        sx={{ mr: 1 }}
                      >
                        {t("user.profile.save")}
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelClick}
                      >
                        {t("user.profile.cancel")}
                      </MDButton>
                    </MDBox>
                  )}
                </MDBox>

                <Grid container spacing={3}>
                  {/* Full Name */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label={t("user.profile.fullName")}
                          name="name"
                          value={editedData.name}
                          onChange={handleInputChange}
                          variant="outlined"
                        />
                      ) : (
                        <MDBox>
                          <MDTypography variant="caption" color="text.secondary">
                            {t("user.profile.fullName")}
                          </MDTypography>
                          <MDTypography variant="body1">{employee?.name}</MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label={t("user.profile.email")}
                          name="email"
                          value={editedData.email}
                          onChange={handleInputChange}
                          variant="outlined"
                          type="email"
                        />
                      ) : (
                        <MDBox>
                          <MDTypography variant="caption" color="text.secondary">
                            {t("user.profile.email")}
                          </MDTypography>
                          <MDTypography variant="body1">{employee?.email}</MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Phone Number */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label={t("user.profile.phoneNumber")}
                          name="phoneNumber"
                          value={editedData.phoneNumber}
                          onChange={handleInputChange}
                          variant="outlined"
                        />
                      ) : (
                        <MDBox>
                          <MDTypography variant="caption" color="text.secondary">
                            {t("user.profile.phoneNumber")}
                          </MDTypography>
                          <MDTypography variant="body1">
                            {employee?.phoneNumber || "Not provided"}
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Date of Birth */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      {isEditing ? (
                        <TextField
                          fullWidth
                          label={t("user.profile.dob")}
                          name="dob"
                          type="date"
                          value={editedData.dob || ""}
                          onChange={handleInputChange}
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      ) : (
                        <MDBox>
                          <MDTypography variant="caption" color="text.secondary">
                            {t("user.profile.dob")}
                          </MDTypography>
                          <MDTypography variant="body1">
                            {employee?.dob
                              ? new Date(employee.dob).toLocaleDateString()
                              : "Not provided"}
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Language */}
                  <Grid item xs={12} md={6}>
                    <MDBox mb={2}>
                      {isEditing ? (
                        <TextField
                          select
                          fullWidth
                          label={t("user.profile.language")}
                          name="language"
                          value={editedData.language || "en"}
                          onChange={handleInputChange}
                          variant="outlined"
                          sx={fieldStyles}
                        >
                          <MenuItem value="en">{t("user.profile.english")}</MenuItem>
                          <MenuItem value="ar">{t("user.profile.arabic")}</MenuItem>
                        </TextField>
                      ) : (
                        <MDBox>
                          <MDTypography variant="caption" color="text.secondary">
                            {t("user.profile.language")}
                          </MDTypography>
                          <MDTypography variant="body1">
                            {employee?.language === "ar"
                              ? t("user.profile.arabic")
                              : t("user.profile.english")}
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Grid>

                  {/* Change Password Button (Only when Editing) */}
                  {isEditing && (
                    <>
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        sx={{ ...fieldStyles, marginLeft: 3, marginTop: 3 }}
                      >
                        <MDButton
                          variant="outlined"
                          color="primary"
                          onClick={() => setOpenPasswordDialog(true)}
                          startIcon={<LockReset />}
                        >
                          {t("user.profile.changePassword")}
                        </MDButton>
                      </MDBox>

                      {/* Password Change Dialog */}
                      <Dialog
                        open={openPasswordDialog}
                        onClose={() => setOpenPasswordDialog(false)}
                      >
                        <DialogTitle>{t("user.profile.changePassword")}</DialogTitle>
                        <DialogContent>
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label={t("user.profile.currentPassword")}
                                name="currentPassword"
                                value={passwordData.currentPassword}
                                onChange={handlePasswordChange}
                                variant="outlined"
                                type="password"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label={t("user.profile.newPassword")}
                                name="newPassword"
                                value={passwordData.newPassword}
                                onChange={handlePasswordChange}
                                variant="outlined"
                                type="password"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label={t("user.profile.confirmPassword")}
                                name="confirmPassword"
                                value={passwordData.confirmPassword}
                                onChange={handlePasswordChange}
                                variant="outlined"
                                type="password"
                              />
                            </Grid>
                          </Grid>
                        </DialogContent>
                        {passwordLoading ? (
                          <Box display="flex" justifyContent="flex-end" p={1}>
                            <CircularProgress size={20} />
                          </Box>
                        ) : (
                          <DialogActions>
                            <Button onClick={() => setOpenPasswordDialog(false)} color="secondary">
                              Cancel
                            </Button>
                            <Button
                              onClick={handlePasswordSubmit}
                              color="primary"
                              disabled={
                                !passwordData.currentPassword ||
                                !passwordData.newPassword ||
                                passwordData.newPassword !== passwordData.confirmPassword
                              }
                            >
                              {t("user.profile.updatePassword")}
                            </Button>
                          </DialogActions>
                        )}
                      </Dialog>
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
        {/* Snackbar for Notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000} // Show for 2 seconds
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </>
  );
}

export default Overview;
